// Copyright (c) 2026 Broadcom. All rights reserved.
// Broadcom Confidential. The term "Broadcom" refers to Broadcom Inc. and/or its subsidiaries.
// Generated by: maverick-file-header, license, codestyle, codequality, clean-code, ai-specific, technology-stack, maverick-file-creation-log
//
// SSO Debug Page — CDA_DevTeam only.
//
// Accessible only via direct URL: /debug
// Not linked from any navigation element — intentionally hidden from regular users.
//
// Sections:
//   1. OIDC Configuration  — what the backend is configured with
//   2. Session Info        — current user's token / session state
//   3. Auth Event Log      — rolling history of every SSO step (newest first)
//   4. Connectivity Probes — live HEAD requests to each AuthHub endpoint

import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from './context/AuthContext';
import { authFetch } from './services/api';
import './App.css';
import './DebugPage.css';

const API_DEBUG     = '/api/auth/debug';
const API_CLEAR_LOG = '/api/auth/debug/clear-log';

// ── Helpers ───────────────────────────────────────────────────────────────────

const ERROR_TYPES = new Set([
  'TOKEN_EXCHANGE_ERROR',
  'STATE_MISMATCH',
  'NO_SESSION_STATE',
  'AUTHHUB_ERROR',
  'USERINFO_FETCH_ERROR',
]);

const SUCCESS_TYPES = new Set([
  'TOKEN_EXCHANGE_SUCCESS',
  'SESSION_USER_BUILT',
  'BYPASS_LOGIN',
  'OIDC_CLIENT_INIT',
]);

const WARN_TYPES = new Set([
  'SESSION_EXPIRED',
  'USERINFO_FETCH_ERROR',
]);

function rowClass(type) {
  if (ERROR_TYPES.has(type))   return 'row-error';
  if (SUCCESS_TYPES.has(type)) return 'row-success';
  if (WARN_TYPES.has(type))    return 'row-warn';
  return 'row-info';
}

function eventBadgeClass(type) {
  if (ERROR_TYPES.has(type))   return 'badge badge-error';
  if (SUCCESS_TYPES.has(type)) return 'badge badge-success';
  if (WARN_TYPES.has(type))    return 'badge badge-warn';
  return 'badge badge-info';
}

function formatTimestamp(ts) {
  if (!ts) return '—';
  try {
    return new Date(ts).toLocaleString();
  } catch {
    return ts;
  }
}

function detailsWithoutMeta(event) {
  // eslint-disable-next-line no-unused-vars
  const { timestamp, type, __network, ...rest } = event;
  const output = { ...rest };
  // Surface the network block under the user-visible key
  if (__network) output.__network_diagnostics = __network;
  if (Object.keys(output).length === 0) return null;
  return JSON.stringify(output, null, 2);
}


// ── Sub-components ────────────────────────────────────────────────────────────

function SectionCard({ title, defaultOpen = true, children, action }) {
  const [open, setOpen] = useState(defaultOpen);
  const toggle = () => setOpen((o) => !o);
  return (
    <div className="debug-section">
      <button
        type="button"
        className="debug-section-header"
        onClick={toggle}
      >
        <span className="debug-section-title">{title}</span>
        <div className="debug-section-header-right">
          {action}
          <span className="debug-section-toggle">{open ? '▲' : '▼'}</span>
        </div>
      </button>
      {open && <div className="debug-section-body">{children}</div>}
    </div>
  );
}

function KvRow({ label, value, boolValue }) {
  let cls = 'debug-kv-value';
  let display = value;
  if (boolValue === true)  { cls += ' is-true';  display = 'true'; }
  if (boolValue === false) { cls += ' is-false'; display = 'false'; }
  return (
    <>
      <div className="debug-kv-key">{label}</div>
      <div className={cls}>{display ?? '—'}</div>
    </>
  );
}

// ── Section: OIDC Config ──────────────────────────────────────────────────────

function OidcConfigSection({ config }) {
  if (!config) return null;
  return (
    <SectionCard title="OIDC Configuration">
      <div className="debug-kv-grid">
        <KvRow label="Bypass Mode"            boolValue={config.bypassEnabled} />
        <KvRow label="Issuer"                 value={config.issuer} />
        <KvRow label="Authorization Endpoint" value={config.authorizationEndpoint} />
        <KvRow label="Token Endpoint"         value={config.tokenEndpoint} />
        <KvRow label="UserInfo Endpoint"      value={config.userinfoEndpoint} />
        <KvRow label="JWKS URI"               value={config.jwksUri} />
        <KvRow label="Redirect URI"           value={config.redirectUri} />
        <KvRow label="Scopes"                 value={config.scopes} />
        <KvRow label="Client ID"              value={config.clientId} />
      </div>
    </SectionCard>
  );
}

// ── Section: Session Info ─────────────────────────────────────────────────────

function SessionInfoSection({ session, serverInfo }) {
  if (!session) return null;
  return (
    <SectionCard title="Session &amp; Server Info">
      <div className="debug-two-col">
        <div>
          <p className="debug-col-label">Current Session</p>
          <div className="debug-kv-grid">
            <KvRow label="Email"        value={session.email} />
            <KvRow label="Username"     value={session.username} />
            <KvRow label="Name"         value={[session.firstName, session.lastName].filter(Boolean).join(' ')} />
            <KvRow label="Groups"       value={session.groups?.join(', ') || '(none)'} />
            <KvRow label="CDA Role"     value={session.cdaRole} />
            <KvRow label="Logged in at" value={formatTimestamp(session.loginAt)} />
            <KvRow label="Token expiry" value={session.tokenExpiry ? formatTimestamp(session.tokenExpiry) : 'N/A (bypass)'} />
          </div>
        </div>
        {serverInfo && (
          <div>
            <p className="debug-col-label">Server</p>
            <div className="debug-kv-grid">
              <KvRow label="NODE_ENV"        value={serverInfo.nodeEnv} />
              <KvRow label="Port"            value={serverInfo.port} />
              <KvRow label="Cookie SameSite" value={serverInfo.cookieSameSite} />
              <KvRow label="Cookie Secure"   value={serverInfo.cookieSecure} />
              <KvRow label="Login Redirect"  value={serverInfo.loginRedirectUrl} />
              <KvRow label="Uptime"          value={`${serverInfo.uptimeSeconds}s`} />
            </div>
          </div>
        )}
      </div>
    </SectionCard>
  );
}

// ── Auth event log helpers ────────────────────────────────────────────────────

/**
 * Build a per-index elapsed-ms map measuring time from each flow's
 * LOGIN_INITIATED (or BYPASS_LOGIN) to every subsequent event.
 *
 * Events arrive newest-first so we scan oldest-to-newest (end → start).
 * Whenever LOGIN_INITIATED / BYPASS_LOGIN is encountered, it anchors a new
 * flow start.  Every event from that point forward gets:
 *   elapsed = event.timestamp − flowStart.timestamp
 *
 * The SESSION_USER_BUILT entry therefore shows the total login wall-clock time.
 * Events that predate any LOGIN_INITIATED get null → displayed as "—".
 */
function buildFlowElapsedMap(events) {
  const elapsedMap  = new Array(events.length).fill(null);
  let   flowStartMs = null;

  for (let i = events.length - 1; i >= 0; i--) {
    const evt = events[i];

    // Only OIDC flows are timed — anchor at LOGIN_INITIATED.
    // BYPASS_LOGIN is instantaneous and has no meaningful flow duration.
    if (evt.type === 'LOGIN_INITIATED') {
      flowStartMs = new Date(evt.timestamp).getTime();
    }

    if (flowStartMs !== null) {
      elapsedMap[i] = new Date(evt.timestamp).getTime() - flowStartMs;
    }

    // Flow is complete at SESSION_USER_BUILT. Reset so that LOGOUT and any
    // other post-session events don't inherit this flow's start time.
    if (evt.type === 'SESSION_USER_BUILT') {
      flowStartMs = null;
    }
  }
  return elapsedMap;
}

function flowElapsedClass(ms, eventType) {
  const isTotal = eventType === 'SESSION_USER_BUILT' || eventType === 'BYPASS_LOGIN';
  if (ms == null || ms === 0) return 'debug-muted';
  if (isTotal && ms > 5000)  return 'debug-flow-elapsed debug-flow-total-slow';
  if (isTotal && ms > 2000)  return 'debug-flow-elapsed debug-flow-total-warn';
  if (isTotal)               return 'debug-flow-elapsed debug-flow-total-ok';
  return 'debug-flow-elapsed';
}

function formatFlowElapsed(ms, eventType) {
  if (ms == null) return '—';
  if (ms === 0)   return 'start';
  const isTotal = eventType === 'SESSION_USER_BUILT' || eventType === 'BYPASS_LOGIN';
  const label   = ms < 1000
    ? `${ms} ms`
    : `${(ms / 1000).toFixed(2)} s`;
  return isTotal ? `${label} total` : label;
}

/**
 * Build a per-index user map so every event in a login flow shows the user
 * who triggered it — even error events that fire before the user is known.
 *
 * Strategy (events arrive newest-first):
 *   • When SESSION_USER_BUILT / BYPASS_LOGIN is seen, latch the email.
 *   • Apply that email to every subsequent (older) event in the same flow.
 *   • Reset at LOGIN_INITIATED — the next older flow starts fresh.
 *   • Failed flows (no SESSION_USER_BUILT before the next LOGIN_INITIATED)
 *     get null → shown as "—".
 */
function buildUserFlowMap(events) {
  const userMap   = new Array(events.length).fill(null);
  let pendingUser = null;

  for (let i = 0; i < events.length; i++) {
    const evt = events[i];

    if (evt.type === 'SESSION_USER_BUILT' || evt.type === 'BYPASS_LOGIN' || evt.type === 'LOGOUT') {
      pendingUser = evt.email || evt.username || null;
    }

    userMap[i] = pendingUser;

    if (evt.type === 'LOGIN_INITIATED') {
      pendingUser = null; // older flow starts fresh
    }
  }
  return userMap;
}


// ── Section: Auth Event Log ───────────────────────────────────────────────────

function AuthEventLogSection({ events, onClear, clearing }) {
  const clearAction = (
    <button
      className="debug-btn-clear"
      onClick={(e) => { e.stopPropagation(); onClear(); }}
      disabled={clearing || !events?.length}
      title="Clear the in-memory auth event log"
    >
      {clearing ? 'Clearing…' : 'Clear Log'}
    </button>
  );

  return (
    <SectionCard title={`Auth Event Log (${events?.length ?? 0} entries)`} action={clearAction}>
      {events?.length > 0 ? (
        <>
          <div className="debug-table-wrap">
            <table className="debug-table">
              <thead>
                <tr>
                  <th style={{ width: 165 }}>Timestamp</th>
                  <th style={{ width: 195 }}>Event Type</th>
                  <th style={{ width: 145 }}>Network Gap / Server Time</th>
                  <th style={{ width: 115 }}>Flow Elapsed</th>
                  <th style={{ width: 220 }}>User</th>
                  <th>Details</th>
                </tr>
              </thead>
              <tbody>
                {(() => {
                  const userMap    = buildUserFlowMap(events);
                  const elapsedMap = buildFlowElapsedMap(events);
                  return events.map((evt, idx) => {
                    const details    = detailsWithoutMeta(evt);
                    const rowKey     = `${evt.timestamp}-${evt.type}`;
                    const durMs      = msSincePrev(events, idx);
                    const durCls     = durationCellClass(durMs, evt.type);
                    const user       = userMap[idx];
                    const elapsedMs  = elapsedMap[idx];
                    const elapsedCls = flowElapsedClass(elapsedMs, evt.type);
                    return (
                      <tr key={rowKey} className={rowClass(evt.type)}>
                        <td className="debug-td-mono debug-td-sm">
                          {formatTimestamp(evt.timestamp)}
                        </td>
                        <td>
                          <span className={eventBadgeClass(evt.type)}>{evt.type}</span>
                        </td>
                        <td className={`debug-td-mono ${durCls}`}>
                          <NetworkGapCell
                            durMs={durMs}
                            eventType={evt.type}
                            serverMs={evt.__network?.serverProcessingMs}
                          />
                        </td>
                        <td className={`debug-td-mono ${elapsedCls}`}>
                          {formatFlowElapsed(elapsedMs, evt.type)}
                        </td>
                        <td className="debug-td-user">
                          {user
                            ? <span className="debug-user-chip" title={user}>{user}</span>
                            : elapsedMs !== null
                              ? <span
                                  className="debug-user-chip debug-user-chip--unknown"
                                  title="Login failed before AuthHub returned the user's identity — email unavailable"
                                >
                                  Unknown
                                </span>
                              : <span className="debug-muted">—</span>
                          }
                        </td>
                        <td>
                          {details ? (
                            <pre className="event-details">{details}</pre>
                          ) : (
                            <span className="debug-muted">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  });
                })()}
              </tbody>
            </table>
          </div>
          <p className="debug-flow-footnote">
            * Duration for CALLBACK_RECEIVED includes time the user spent on the AuthHub login page
            (entering credentials, MFA, etc.) — this is not a server or network metric.
          </p>
        </>
      ) : (
        <p className="debug-empty">No auth events recorded yet. Trigger a login attempt to populate this log.</p>
      )}
    </SectionCard>
  );
}

// ── Section: Connectivity Probes ──────────────────────────────────────────────

function ConnectivitySection({ probes }) {
  if (!probes?.length) return null;
  return (
    <SectionCard title="Live Connectivity Probes">
      <div className="debug-table-wrap">
        <table className="debug-table">
          <thead>
            <tr>
              <th style={{ width: 220 }}>Endpoint</th>
              <th style={{ width: 120 }}>Status</th>
              <th style={{ width: 110 }}>HTTP Code</th>
              <th style={{ width: 110 }}>Latency</th>
              <th>URL / Error</th>
            </tr>
          </thead>
          <tbody>
            {probes.map((p) => (
              <tr key={p.label}>
                <td className="debug-td-strong">{p.label}</td>
                <td>
                  {p.reachable
                    ? <span className="badge badge-success">Reachable</span>
                    : <span className="badge badge-error">Unreachable</span>
                  }
                </td>
                <td className="debug-td-mono">{p.statusCode ?? '—'}</td>
                <td className="debug-td-mono debug-muted">{p.latencyMs == null ? '—' : `${p.latencyMs} ms`}</td>
                <td style={{ wordBreak: 'break-all' }}>
                  {p.error
                    ? <span className="debug-error-text">{p.error}</span>
                    : <span className="debug-url-text">{p.url ?? '—'}</span>
                  }
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </SectionCard>
  );
}

// ── Helpers: auth event log duration column ───────────────────────────────────

/**
 * Events arrive newest-first; events[idx+1] is the chronologically earlier step.
 * Returns the milliseconds elapsed since that prior step, or null for the first event.
 */
function msSincePrev(events, idx) {
  if (idx >= events.length - 1) return null;
  return new Date(events[idx].timestamp).getTime()
       - new Date(events[idx + 1].timestamp).getTime();
}

function durationCellClass(ms, eventType) {
  if (ms == null || eventType === 'LOGOUT' || eventType === 'LOGIN_INITIATED') return 'debug-muted';
  if (eventType === 'CALLBACK_RECEIVED') return 'debug-duration-user';
  if (ms > 2000) return 'debug-duration-slow';
  if (ms > 500)  return 'debug-duration-warn';
  return 'debug-duration-ok';
}

function formatDurationMs(ms, eventType) {
  if (ms == null || eventType === 'LOGOUT' || eventType === 'LOGIN_INITIATED') return '—';
  const suffix = eventType === 'CALLBACK_RECEIVED' ? ' *' : '';
  if (ms < 1000) return `${ms} ms${suffix}`;
  return `${(ms / 1000).toFixed(2)} s${suffix}`;
}

/**
 * Renders the timing cell split into total network gap and server processing time.
 * When serverMs is present the contrast (e.g. "4.38 s gap / 3 ms server") makes
 * it immediately obvious that the app code is not the bottleneck.
 */
function NetworkGapCell({ durMs, eventType, serverMs }) {
  if (durMs == null) return <span className="debug-muted">—</span>;
  const gapLabel = formatDurationMs(durMs, eventType);
  if (serverMs == null) return <span>{gapLabel}</span>;
  const srvLabel = serverMs < 1 ? '< 1 ms server' : `${serverMs} ms server`;
  return (
    <>
      {gapLabel}
      <span className="debug-server-time">{srvLabel}</span>
    </>
  );
}

// ── Section: Browser Network Timing ──────────────────────────────────────────


// ── Main page component ───────────────────────────────────────────────────────

const DebugPage = () => {
  const { logout } = useAuth();

  const [data,     setData]     = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [fetchErr, setFetchErr] = useState(null);
  const [clearing, setClearing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setFetchErr(null);
    try {
      const res = await authFetch(API_DEBUG);
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        setFetchErr(body.error || `HTTP ${res.status}`);
        return;
      }
      setData(await res.json());
    } catch (err) {
      setFetchErr(err.message || 'Network error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleClearLog = useCallback(async () => {
    setClearing(true);
    try {
      await authFetch(API_CLEAR_LOG, { method: 'POST' });
      await load();
    } finally {
      setClearing(false);
    }
  }, [load]);

  return (
    <div className="App">

      {/* ── Navbar — matches MainApp exactly ── */}
      <header className="App-header">
        <div className="header-left">
          <div className="logo-container">
            <img src="/broadcom-logo.jpg" alt="Broadcom" className="header-logo" />
          </div>
          <h1>CodeDisk Application</h1>
        </div>
        <div className="header-right">
          <button
            className="debug-refresh-btn"
            onClick={load}
            disabled={loading}
          >
            {loading ? 'Refreshing…' : 'Refresh'}
          </button>
          <button className="logout-btn" onClick={logout}>
            Logout
          </button>
        </div>
      </header>

      {/* ── Sub-header: page identity + snapshot time ── */}
      <div className="debug-subheader">
        <div className="debug-subheader-left">
          <span className="debug-subheader-title">SSO Debug Console</span>
          <span className="debug-subheader-badge">DevTeam Only</span>
        </div>
        {data?.generatedAt && (
          <span className="debug-subheader-snapshot">
            Snapshot: {formatTimestamp(data.generatedAt)}
          </span>
        )}
      </div>

      {/* ── Main content ── */}
      <div className="main-content debug-main">

        {loading && !data && (
          <div className="debug-loading">Loading diagnostic data…</div>
        )}

        {fetchErr && (
          <div className="debug-fetch-error">
            Failed to load diagnostic data: <strong>{fetchErr}</strong>
          </div>
        )}

        {data && (
          <div className="debug-sections">
            <OidcConfigSection    config={data.oidcConfig} />
            <SessionInfoSection   session={data.sessionInfo} serverInfo={data.serverInfo} />
            <ConnectivitySection  probes={data.connectivityProbes} />
            <AuthEventLogSection
              events={data.authEvents}
              onClear={handleClearLog}
              clearing={clearing}
            />
          </div>
        )}
      </div>

      {/* ── Footer — matches MainApp exactly ── */}
      <footer className="App-footer">
        <p>© 2024 Broadcom. All Rights Reserved.</p>
      </footer>

    </div>
  );
};

export default DebugPage;
